import React from "react";
import { useLocation } from "react-router-dom";
import { device } from "../../../../utils/media-queries";
import { rem } from "polished";
import { Col, Container, Row } from "react-grid-system";
import styled from "styled-components";

import { config } from "../../../../config";
import {
  Heading1,
  Heading2,
  HeroVideoContainer,
  Section,
} from "../../../../components/themed-v2/atoms";
import {
  DownloadAppPanel,
  HeroPanel,
  RatingAppStore,
} from "../../../../components/themed-v2/molecules";
import {
  CategoryTilesCarousel,
  CustomerReviews,
  FeaturedCarousel,
  FeaturedPanel,
  ProcessWidget,
} from "../../../../components/themed-v2/organisms";
import { SearchFormTreatmentsContainer } from "../../../../containers/search/search-form-treatments";
import { useHomePageData } from "./use-home-page-data";
import { HomeSubscribeModalContainer } from "../../../../containers/home/home-subscribe";

const RatingAppStoreStyled = styled(RatingAppStore)`
  margin-top: ${rem("24px")};
`;

const SearchBox = styled.div`
  width: ${rem("320px")};
  border-radius: ${rem("8px")};
  padding: ${rem("14px")};
  margin-top: ${rem("24px")};
  display: flex;
  flex-direction: column;
  background-color: ${({ theme }) => theme.colours.sectionBackground.blueGrey};

  @media ${device.tablet} {
    margin-top: ${rem("31px")};
  }
`;

const SearchTitle = styled(Heading2)`
  text-align: center;
`;

const SpecialLink = styled.a`
  color: ${({ theme }) => theme.colours.textMain};
  text-decoration: none;
  & > span {
    text-decoration: underline;
  }

  :hover {
    & > span {
      text-decoration: none;
    }
  }
`;

interface IProps {
  useData?: typeof useHomePageData;
}

export const HomePage = ({
  useData = useHomePageData,
}: IProps): JSX.Element => {
  const { pathname } = useLocation();

  const {
    featureTreatments,
    mostPopularTreatments,
    reviews,
    handleClickButton,
    specialLink,
  } = useData();

  const paragraphs: React.ComponentProps<typeof HeroPanel>["paragraphs"] = [
    "Available in London, Manchester, Surrey & The Cotswolds",
  ];

  if (specialLink) {
    paragraphs.push(
      <SpecialLink href={specialLink} target="_blank">
        Now also available in Meribel and Courchevel. Click <span>here</span> to book.
      </SpecialLink>,
    );
  }
  paragraphs.push(
    <SpecialLink href="/south-of-france">
      Now also available to book in South of France. Click <span>here</span> to book.
    </SpecialLink>,
  );

  return (
    <>
      <HeroVideoContainer video={config.heroVideoUrl}>
        <Container>
          <Row justify="center">
            <Col lg={8}>
              <HeroPanel
                heading="Book five star beauty services, direct to your door"
                paragraphs={paragraphs}
              >
                <RatingAppStoreStyled />
                <SearchBox>
                  <SearchTitle>Browse availability</SearchTitle>
                  <SearchFormTreatmentsContainer url={pathname} />
                </SearchBox>
              </HeroPanel>
            </Col>
          </Row>
        </Container>
      </HeroVideoContainer>

      <Section colour="white">
        <Container>
          <Row>
            <Col>
              <CategoryTilesCarousel />
            </Col>
          </Row>
        </Container>
      </Section>

      <Section colour="white" noTopPadding>
        <Container>
          <Row>
            <Col>
              <Heading1>Our Most Popular Treatments</Heading1>
              <FeaturedCarousel
                carouselKey="most-popular-treatments"
                items={mostPopularTreatments}
              />
            </Col>
          </Row>
        </Container>
      </Section>

      <Section colour="blueGrey">
        <ProcessWidget handleClickButton={handleClickButton} />
      </Section>

      <Section colour="white">
        <Container>
          <Row>
            <Col>
              <Heading1>Featured Treatments</Heading1>
              <FeaturedCarousel
                carouselKey="featured-treatments"
                items={featureTreatments}
              />
            </Col>
          </Row>
        </Container>
      </Section>

      <Section colour="darkGrey">
        <CustomerReviews
          heading="Experience the at-home beauty app that customers rate five stars"
          reviews={reviews}
        />
        <FeaturedPanel />
      </Section>

      <Section colour="white">
        <Container>
          <Row justify="center">
            <DownloadAppPanel />
          </Row>
        </Container>
      </Section>
      <HomeSubscribeModalContainer />
    </>
  );
};
